import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/PasswordInput';
import { CompanyFields, EMPTY_COMPANY, formatApiError } from '../components/CompanyFields';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [accountType, setAccountType] = useState('privato');
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const isCompany = accountType === 'azienda';
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email,
        password: formData.password,
        phone: formData.phone.trim(),
        account_type: accountType,
        company: isCompany ? company : null
      });
      login(response.data.user, response.data.session_token);
      navigate('/');
    } catch (error) {
      setError(formatApiError(error.response?.data?.detail, 'Errore durante la registrazione'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12">
      <div className={`w-full ${isCompany ? 'max-w-lg' : 'max-w-md'} px-6`}>
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl mb-2">Registrati</h1>
          <p className="text-[#64748b]">Crea il tuo account Equus Prime</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 border border-[#0a1628]" role="radiogroup" data-testid="account-type-toggle">
            {[['privato', 'Privato'], ['azienda', 'Azienda']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={accountType === value}
                onClick={() => setAccountType(value)}
                className={`py-3 text-sm font-medium transition-colors ${accountType === value ? 'bg-[#0a1628] text-white' : 'bg-white text-[#0a1628] hover:bg-[#f8f9fa]'}`}
                data-testid={`account-type-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
          {isCompany && <CompanyFields value={company} onChange={setCompany} />}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{isCompany ? 'Nome del referente' : 'Nome'}</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field" required pattern=".*\S.*" title="Inserisci il nome" autoComplete="given-name" data-testid="register-first-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{isCompany ? 'Cognome del referente' : 'Cognome'}</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field" required pattern=".*\S.*" title="Inserisci il cognome" autoComplete="family-name" data-testid="register-last-name" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              required
              data-testid="register-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Cellulare</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              required
              pattern="[+]?[0-9 ]{8,16}"
              title="Inserisci un numero di cellulare valido (solo cifre, es. 333 1234567)"
              placeholder="es. 333 1234567"
              autoComplete="tel"
              data-testid="register-phone"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <PasswordInput
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              autoComplete="new-password"
              testId="register-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Conferma Password</label>
            <PasswordInput
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              testId="register-confirm-password"
            />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? 'Registrazione...' : 'Registrati'}
          </button>
        </form>

        <div className="mt-6 p-4 bg-[#f8f9fa] border-l-4 border-[#c9a227]">
          <p className="text-sm text-[#64748b]">
            <strong>Nota:</strong> Dopo la registrazione, invia un documento d'identità a <a href="mailto:info@equusprime.it" className="text-[#c9a227]">info@equusprime.it</a> per attivare il tuo account e poter partecipare alle aste.
          </p>
        </div>

        <p className="text-center mt-6 text-[#64748b]">
          Hai già un account? <a href="/login" className="text-[#c9a227]">Accedi</a>
        </p>
      </div>
    </div>
  );
};


export default RegisterPage;
